import React from "react";
import { Modal, List, Button, Typography, Space, Popconfirm, Empty, Tag, message } from "antd";
import { DeleteOutlined, FileTextOutlined } from "@ant-design/icons";
import type { AppConfigTemplate } from "../types/api";
import { useDeleteAppConfigTemplateMutation, useWorkerTemplateNameMap } from "../hooks/useAppsQueries";
import { parseJsonArray } from "../helpers/helpers.app";

const { Text, Paragraph } = Typography;

interface LoadTemplateModalProps {
  open: boolean;
  onClose: () => void;
  templates: AppConfigTemplate[];
  loading: boolean;
  onSelect: (template: AppConfigTemplate) => void;
}

export const LoadTemplateModal: React.FC<LoadTemplateModalProps> = ({
  open,
  onClose,
  templates,
  loading,
  onSelect,
}) => {
  const deleteMutation = useDeleteAppConfigTemplateMutation();
  const workerTemplateNameById = useWorkerTemplateNameMap(
    templates.map((t) => t.templateId),
  );

  return (
    <Modal
      title="Завантажити пресет роутингу"
      open={open}
      onCancel={onClose}
      footer={
        <Button onClick={onClose} type="primary">
          Скасувати
        </Button>
      }
      width={560}
    >
      {templates.length === 0 && !loading ? (
        <Empty description="Немає збережених пресетів роутингу" />
      ) : (
        <List
          loading={loading}
          dataSource={templates}
          renderItem={(tmpl) => (
            <List.Item
              actions={[
                <Button
                  type="link"
                  onClick={() => {
                    onSelect(tmpl);
                    onClose();
                  }}
                >
                  Застосувати
                </Button>,
                <Popconfirm
                  title="Видалити пресет?"
                  onConfirm={async () => {
                    try {
                      await deleteMutation.mutateAsync(tmpl.id);
                      message.success("Пресет видалено");
                    } catch {
                      message.error("Помилка видалення пресету");
                    }
                  }}
                  okText="Так"
                  cancelText="Ні"
                >
                  <Button
                    type="link"
                    danger
                    icon={<DeleteOutlined />}
                    loading={deleteMutation.isPending}
                  />
                </Popconfirm>,
              ]}
            >
              <List.Item.Meta
                avatar={<FileTextOutlined style={{ fontSize: 24, color: "#1890ff" }} />}
                title={
                  <Space>
                    {tmpl.name}
                    {tmpl.greyType && (
                      <Tag color={tmpl.greyType === "websocket" ? "purple" : "blue"}>
                        {tmpl.greyType}
                      </Tag>
                    )}
                  </Space>
                }
                description={
                  <div>
                    {tmpl.description && (
                      <Paragraph type="secondary" style={{ margin: 0 }} ellipsis={{ rows: 1 }}>
                        {tmpl.description}
                      </Paragraph>
                    )}
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {tmpl.templateId
                        ? `Worker: ${workerTemplateNameById.get(tmpl.templateId) || tmpl.templateId} · `
                        : ""}
                      {parseJsonArray(tmpl.endpointMappings).length} ендпоінтів · {parseJsonArray(tmpl.landingRoutes).length} маршрутів
                    </Text>
                  </div>
                }
              />
            </List.Item>
          )}
        />
      )}
    </Modal>
  );
};
